import ExpiredId from '../../assets/images/expired-id.webp';
import ChatWithAgent from '../../assets/images/chat-with-agent.webp';
import InfoTriangle from '../../assets/images/info-triangle.webp';
import InvalidImage from '../../assets/images/invalid-image.webp';
import BrokenId from '../../assets/images/broken-id.webp';
import useTranslation from '../../localization/i18n.context';
import LuckyDeeplink from '../../constants/lucky.deeplink';
import { CloseWebviewEvent } from '../../sdk/close.webview.event';
import { DeeplinkEvent } from '../../sdk/deeplink.event';
import InfoFeedbackScreen from '../../components/shared/InfoFeedbackScreen/InfoFeedbackScreen';
import { useEffect } from 'preact/hooks';
import NavigationUtils from '../../utils/navigation.utils';
import OnboardingStatusUrlUtils from '../../utils/onboarding-status-url.utils';
import { TrackingEvent } from '../../sdk/tracking.event';

const navigateToKyc = () => {
  NavigationUtils.navigate(NavigationUtils.Routes.KYC);
};

const finishWithError = () => {
  CloseWebviewEvent.finishWithError();
};

const OnboardingStatusConfig = {
  [OnboardingStatusUrlUtils.OnboardingStatusType.DUPLICATE_ID]: {
    image: ChatWithAgent,
    title:
      "It looks like there's already a {card name} card associated with your ID",
    subtitle:
      "Connect with customer support below and we'll help you access your account",
    ctaText: 'Connect with support',
    eventName: 'duplicated_ID',
    ctaAction: () => {
      // TODO : it is required to change in the future when we implement another client.
      DeeplinkEvent.open(LuckyDeeplink.FAQS);
    },
    onCloseClick: finishWithError,
  },

  [OnboardingStatusUrlUtils.OnboardingStatusType.EXPIRED_ID]: {
    image: ExpiredId,
    title: 'It looks like your ID has expired!',
    subtitle:
      "Please renew your ID and re-apply, or check you've used the right ID and try again below",
    ctaText: 'Try Again',
    eventName: 'expired_ID',
    ctaAction: navigateToKyc,
    onCloseClick: finishWithError,
  },
  [OnboardingStatusUrlUtils.OnboardingStatusType.UNMATCHING_DATA]: {
    image: InfoTriangle,
    title: 'Your information does not match',
    subtitle:
      'Please try again and confirm that the name and ID information you enter match the information on the ID image you upload',
    ctaText: 'Try Again',
    eventName: 'information_mismatch',
    ctaAction: navigateToKyc,
    onCloseClick: finishWithError,
  },
  [OnboardingStatusUrlUtils.OnboardingStatusType.INVALID_ID_NUMBER]: {
    image: InfoTriangle,
    title: 'It looks like your uploaded image is not a valid ID',
    subtitle:
      'Please re-try uploading your image and ensure you use your Egyptian national ID',
    ctaText: 'Try Again',
    eventName: 'invalid_id',
    ctaAction: navigateToKyc,
    onCloseClick: finishWithError,
  },
  [OnboardingStatusUrlUtils.OnboardingStatusType.IMAGE_QUALITY]: {
    image: InvalidImage,
    title: "We can't see the image you uploaded",
    subtitle:
      'Please retake your ID image and ensure that your phone is still, in good light and with no reflections blocking the picture',
    ctaText: 'Try Again',
    eventName: 'invalid_image',
    ctaAction: navigateToKyc,
    onCloseClick: finishWithError,
  },
  [OnboardingStatusUrlUtils.OnboardingStatusType.BROKEN_ID]: {
    image: BrokenId,
    title: 'It looks like your ID is broken',
    subtitle:
      'Please check that your ID is in good condition. If your ID is broken or not readable, please renew your ID and re-apply, or try taking a new picture now',
    ctaText: 'Try Again',
    eventName: 'broken_id',
    ctaAction: navigateToKyc,
    onCloseClick: finishWithError,
  },
};

export default function OnboardingStatusPage({
  organization: organizationCode,
  type,
}) {
  const { t, getOrganization } = useTranslation();

  const data = OnboardingStatusConfig[type];

  useEffect(() => {
    TrackingEvent.trigger(
      TrackingEvent.Events.ONBOARDING_FEEDBACK_SHOW,
      TrackingEvent.Sources.CAAS,
      {
        status: data.eventName,
      },
    );
  }, [type]);

  return (
    <>
      {data && (
        <InfoFeedbackScreen
          onBackClick={data.onBackClick}
          onCloseClick={data.onCloseClick}
          image={data.image}
          title={t(data.title, {
            '{card name}': getOrganization(organizationCode),
          })}
          subtitle={t(data.subtitle, {
            '{card name}': getOrganization(organizationCode),
          })}
          ctaAction={data.ctaAction}
          ctaText={t(data.ctaText)}
        />
      )}
    </>
  );
}
